import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getAllVideosForProfessor } from '../../../Api/authApi.js.js';
import { VideoModal } from '../../../components/VideoModal';
import { Skeleton } from '../../components/Skeleton';
import './videos.css';

const MONTHS_SHORT = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'];

function dateVirtuelleLabel(dateVirtuelle) {
    if (!dateVirtuelle) return '';
    const { jour_virtuel, mois_virtuel, annee_virtuelle } = dateVirtuelle;
    const mois = MONTHS_SHORT[mois_virtuel - 1] || '';
    return `${jour_virtuel} ${mois} ${annee_virtuelle}`;
}

const PALETTE = ['#7AB8E0', '#9B7BE0', '#E0C281', '#7AE0C5', '#E07AAB', '#4ADE9E', '#FF9E7A'];

const videoColor = (id = '') => {
    let h = 0;
    const s = String(id);
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffff;
    return PALETTE[h % PALETTE.length];
};

function VideoThumbnail({ color }) {
    return (
        <div style={{
            height: 110, position: 'relative',
            background: `repeating-linear-gradient(135deg, ${color}22 0px, ${color}22 8px, ${color}10 8px, ${color}10 16px)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div style={{
                width: 42, height: 42, borderRadius: '50%', background: 'rgba(0,0,0,0.35)',
                border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <div style={{ width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderLeft: '11px solid white', marginLeft: 3 }} />
            </div>
        </div>
    );
}

function VideosSkeleton() {
    return (
        <div className="prof-videos-grid" style={{ marginTop: 16 }}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
                <div key={i} className="relief" style={{ borderRadius: 16, overflow: 'hidden' }}>
                    <Skeleton width="100%" height={110} radius={0} />
                    <div style={{ padding: 14 }}>
                        <Skeleton width="80%" height={14.5} radius={4} />
                        <div style={{ marginTop: 8 }}><Skeleton width={70} height={12} radius={4} /></div>
                    </div>
                </div>
            ))}
        </div>
    );
}

const PER_PAGE = 20;

const ActualitesProf = () => {
    const professeurId = useSelector((state) => state.auth.user?.id);

    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [videos, setVideos] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | empty | error

    useEffect(() => {
        if (!professeurId) return;
        setStatus('loading');
        getAllVideosForProfessor(professeurId, page, PER_PAGE)
            .then(res => {
                const data = res?.data || [];
                setVideos(data);
                setLastPage(res?.last_page || 1);
                setTotal(res?.total || 0);
                setStatus(data.length ? 'ready' : 'empty');
            })
            .catch(() => setStatus('error'));
    }, [professeurId, page]);

    return (
        <div className="prof-videos-page">
            <div style={{ padding: '4px 4px 4px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div>
                    <div className="mono" style={{ fontSize: 11, letterSpacing: '0.08em', color: 'var(--text-mute)', textTransform: 'uppercase', marginBottom: 6 }}>
                        Pédagogie
                    </div>
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em' }}>Actualités</h1>
                </div>
                {total > 0 && (
                    <span style={{ fontSize: 12, color: 'var(--text-mute)' }}>{total} actualité{total > 1 ? 's' : ''}</span>
                )}
            </div>

            {status === 'loading' && <VideosSkeleton />}
            {status === 'empty' && (
                <div className="relief" style={{ marginTop: 16, borderRadius: 16, padding: 20, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
                    Aucune actualité disponible.
                </div>
            )}
            {status === 'error' && (
                <div className="relief" style={{ marginTop: 16, borderRadius: 16, padding: 20, textAlign: 'center', color: 'var(--down)', fontSize: 13 }}>
                    Impossible de charger les actualités.
                </div>
            )}

            {status === 'ready' && (
                <>
                    <div className="prof-videos-grid" style={{ marginTop: 16 }}>
                        {videos.map((v, index) => (
                            <div key={`${v.id}_${index}`} className="relief" style={{ borderRadius: 16, overflow: 'hidden' }}>
                                <button
                                    className="prof-videos-card pill-tap"
                                    onClick={() => setSelectedVideo(v)}
                                >
                                    <VideoThumbnail color={videoColor(v.id)} />
                                    <div style={{ padding: 14, textAlign: 'left' }}>
                                        <div style={{ fontSize: 14.5, fontWeight: 600, lineHeight: 1.3 }}>{v.title}</div>
                                        <div style={{ fontSize: 12, color: 'var(--text-mute)', marginTop: 5 }}>{dateVirtuelleLabel(v.date_virtuelle)}</div>
                                    </div>
                                </button>
                                {v.fiche_pedagogique_path && (
                                    <a
                                        href={'/storage/' + v.fiche_pedagogique_path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="prof-videos-fiche-link"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Voir la fiche pédagogique
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>

                    {lastPage > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 30 }}>
                            <button
                                className="pill-tap prof-videos-pagination-btn"
                                disabled={page <= 1}
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                style={{
                                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                                    background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
                                    color: 'var(--text)', cursor: 'pointer',
                                }}
                            >Précédent</button>
                            <span style={{ fontSize: 12.5, color: 'var(--text-mute)' }}>Page {page} / {lastPage}</span>
                            <button
                                className="pill-tap prof-videos-pagination-btn"
                                disabled={page >= lastPage}
                                onClick={() => setPage(p => Math.min(lastPage, p + 1))}
                                style={{
                                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                                    background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
                                    color: 'var(--text)', cursor: 'pointer',
                                }}
                            >Suivant</button>
                        </div>
                    )}
                </>
            )}

            <VideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} tagLabel={null} showMarketsCta={false} />
        </div>
    );
};

export default ActualitesProf;
